import { App } from "antd"
import React, {
  type Dispatch,
  type SetStateAction,
  useRef,
  useState
} from "react"
import { useTranslation } from "react-i18next"

import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import {
  deleteAutomation,
  disableAutomation,
  duplicateAutomation,
  enableAutomation,
  getPendingCount
} from "../api/services.ts"
import { isEnableBlocked, readErrorDetails } from "../helpers/api-errors"
import type { Automation } from "../types/automation.types"

const replaceRow = (record: Automation) => (current: Automation[]) =>
  current.map((row) => (row.id === record.id ? record : row))

// Pause/Enable, Duplicate and Delete for the overview's rows. Each writes the
// server's answer back into the list rather than guessing at it.
export function useAutomationRowActions(
  setRows: Dispatch<SetStateAction<Automation[]>>
) {
  const { t } = useTranslation()
  const { message, modal, notification } = App.useApp()
  const [togglingIds, setTogglingIds] = useState<ReadonlySet<string>>(new Set())
  const togglingRef = useRef(new Set<string>())
  const runningRef = useRef(new Set<string>())
  const [announcement, setAnnouncement] = useState({ id: 0, text: "" })

  // The toast, and the same words for the overview's live region. A new id
  // re-announces a repeated message.
  const announce = (text: string) =>
    setAnnouncement((previous) => ({ id: previous.id + 1, text }))
  const report = (type: "success" | "error", text: string) => {
    void message[type](text)
    announce(text)
  }

  const setToggling = (id: string, busy: boolean) => {
    if (busy) togglingRef.current.add(id)
    else togglingRef.current.delete(id)
    setTogglingIds(new Set(togglingRef.current))
  }

  const requestToggle = async (automation: Automation, next: boolean) => {
    let response
    try {
      response = next
        ? await enableAutomation(automation.id)
        : await disableAutomation(automation.id)
    } catch {
      response = null
    }
    if (response?.ok) {
      setRows(replaceRow(unwrapEnvelope<Automation>(response.data)))
      report(
        "success",
        t(`settingsAutomation.rowActions.${next ? "enabled" : "paused"}`)
      )
      return
    }
    const body = response?.data
    if (isEnableBlocked(body)) {
      const title = t("settingsAutomation.rowActions.enableBlocked", {
        name: automation.name
      })
      announce([title, ...body.reasons].join(" "))
      notification.error({
        message: title,
        description: (
          <ul className={"m-0 list-disc pl-4"}>
            {body.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        )
      })
      return
    }
    report(
      "error",
      readErrorDetails(body).message ||
        t(
          `settingsAutomation.rowActions.${next ? "enableFailed" : "pauseFailed"}`
        )
    )
  }

  const toggle = async (automation: Automation, next: boolean) => {
    if (togglingRef.current.has(automation.id)) return
    setToggling(automation.id, true)
    try {
      await requestToggle(automation, next)
    } finally {
      setToggling(automation.id, false)
    }
  }

  // One run per row and action: a second pick while the first is still
  // running (a slow duplicate, a delete dialog on its way or open) is dropped.
  const runOnce = async (
    action: string,
    id: string,
    work: () => Promise<void>
  ) => {
    const key = `${action}:${id}`
    if (runningRef.current.has(key)) return
    runningRef.current.add(key)
    try {
      await work()
    } finally {
      runningRef.current.delete(key)
    }
  }

  const duplicate = (automation: Automation) =>
    runOnce("duplicate", automation.id, async () => {
      let response
      try {
        response = await duplicateAutomation(automation.id)
      } catch {
        response = null
      }
      if (!response?.ok) {
        report(
          "error",
          readErrorDetails(response?.data).message ||
            t("settingsAutomation.rowActions.duplicateFailed")
        )
        return
      }
      const copy = unwrapEnvelope<Automation>(response.data)
      setRows((current) => [copy, ...current])
      report("success", t("settingsAutomation.rowActions.duplicated"))
    })

  const remove = (automation: Automation) =>
    runOnce("delete", automation.id, () => confirmDelete(automation))

  const confirmDelete = async (automation: Automation) => {
    let pending
    try {
      pending = await getPendingCount(automation.id)
    } catch {
      pending = null
    }
    const count = pending?.ok
      ? (pending.data?.pending_count ?? 0)
      : automation.pending_count
    // A Nurturing automation's upcoming runs, each a send to every lead then in Nurturing.
    const runs = pending?.ok
      ? (pending.data?.pending_runs ?? 0)
      : (automation.pending_runs ?? 0)
    const confirmed = modal.confirm({
      centered: true,
      content: [
        t("settingsAutomation.rowActions.deleteConfirm", {
          name: automation.name
        }),
        count > 0
          ? t("settingsAutomation.rowActions.pendingCancelled", { count })
          : null,
        runs > 0
          ? t("settingsAutomation.rowActions.pendingRunsCancelled", {
              count: runs
            })
          : null,
        t("settingsAutomation.rowActions.historyKept")
      ]
        .filter(Boolean)
        .join(" "),
      okText: t("settingsAutomation.rowActions.delete"),
      okButtonProps: { danger: true },
      cancelText: t("settingsAutomation.rowActions.cancel"),
      onOk: async () => {
        let response
        try {
          response = await deleteAutomation(automation.id)
        } catch {
          response = null
        }
        if (!response?.ok) {
          report(
            "error",
            readErrorDetails(response?.data).message ||
              t("settingsAutomation.rowActions.deleteFailed")
          )
          // A rejected onOk keeps the dialog open for a retry.
          throw response
        }
        setRows((current) => current.filter((row) => row.id !== automation.id))
        report("success", t("settingsAutomation.rowActions.deleted"))
      }
    })
    // Held until the dialog closes, so the row cannot open a second one.
    await confirmed.then(
      () => {},
      () => {}
    )
  }

  return { togglingIds, toggle, duplicate, remove, announcement }
}
